/**
 * Client Portal V2 - Quote Detail Page
 * =====================================
 * Full quote detail with approval/rejection flow.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { apiClient, clientAuth } from "@/lib/clientApi";
import ClientLayout from "@/components/client/ClientLayout";
import {
  ClientLoadingState, ClientErrorState
} from "@/components/client/ClientComponents";
import {
  ArrowLeft, FileText, CheckCircle, XCircle, 
  AlertTriangle, Download, MessageSquare, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function ClientQuoteDetailPage() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [alerts, setAlerts] = useState([]);
  
  // Action states
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [requesting, setRequesting] = useState(false);
  
  // Dialog states
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [clarifyDialogOpen, setClarifyDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  
  // Form values
  const [rejectReason, setRejectReason] = useState("");
  const [clarifyQuestions, setClarifyQuestions] = useState("");
  const [approveNotes, setApproveNotes] = useState("");

  const fetchQuote = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      
      const res = await apiClient.get(`/api/client/quotes/${quoteId}`);
      
      if (res.data?.success) {
        setQuote(res.data.data);
        setAlerts(res.data.alerts || []);
      }
    } catch (err) {
      console.error("Failed to load quote:", err);
      if (err.response?.status === 401) {
        clientAuth.logout();
        return;
      }
      if (err.response?.status === 404) {
        setError("Quote not found");
      } else if (err.response?.status === 403) {
        setError("You don't have access to this quote");
      } else {
        setError("Failed to load quote details");
      }
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => {
    if (!clientAuth.isAuthenticated()) {
      navigate("/client-login");
      return;
    }
    fetchQuote();
  }, [fetchQuote, navigate]);

  const handleApprove = async () => {
    try {
      setApproving(true);
      await apiClient.post(`/api/client/quotes/${quoteId}/approve`, {
        notes: approveNotes || null,
      });
      toast.success("Quote approved successfully");
      setApproveDialogOpen(false);
      fetchQuote();
    } catch (err) {
      console.error("Approve error:", err);
      toast.error(err.response?.data?.detail || "Failed to approve quote");
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }
    
    try {
      setRejecting(true);
      await apiClient.post(`/api/client/quotes/${quoteId}/reject`, {
        reason: rejectReason,
      });
      toast.success("Quote rejected");
      setRejectDialogOpen(false);
      fetchQuote();
    } catch (err) {
      console.error("Reject error:", err);
      toast.error(err.response?.data?.detail || "Failed to reject quote");
    } finally {
      setRejecting(false);
    }
  };

  const handleRequestClarification = async () => {
    if (!clarifyQuestions.trim()) {
      toast.error("Please provide your questions");
      return;
    }
    
    try {
      setRequesting(true);
      await apiClient.post(`/api/client/quotes/${quoteId}/request-clarification`, {
        questions: clarifyQuestions,
      });
      toast.success("Clarification requested");
      setClarifyDialogOpen(false);
      fetchQuote();
    } catch (err) {
      console.error("Clarification error:", err);
      toast.error(err.response?.data?.detail || "Failed to request clarification");
    } finally {
      setRequesting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "-";
    return `£${Number(value).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <ClientLayout>
        <ClientLoadingState message="Loading quote details..." />
      </ClientLayout>
    );
  }

  if (error) {
    return (
      <ClientLayout>
        <ClientErrorState message={error} onRetry={fetchQuote} />
      </ClientLayout>
    );
  }

  if (!quote) return null;

  const canTakeAction = quote.action_required;

  return (
    <ClientLayout>
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate("/client-v2/quotes")}
        className="mb-4 -ml-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Quotes
      </Button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-slate-900">{quote.quote_reference}</h1>
              {quote.status === "Approved" && (
                <Badge className="bg-green-100 text-green-700">Approved</Badge>
              )}
              {quote.status === "Rejected" && (
                <Badge className="bg-red-100 text-red-700">Rejected</Badge>
              )}
              {canTakeAction && (
                <Badge className="bg-orange-100 text-orange-700 ring-2 ring-orange-300">
                  Action Required
                </Badge>
              )}
            </div>
            <p className="text-slate-600">
              From {quote.contractor_name} • Submitted {formatDate(quote.submitted_at)}
            </p>
          </div>
          
          {quote.job && (
            <Link to={`/client-v2/jobs/${quote.job.id}`}>
              <Button variant="outline" size="sm">
                View Linked Job
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Action Required Alert */}
      {canTakeAction && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-orange-600" />
          <span className="text-orange-800 font-medium flex-1">
            This quote requires your approval to proceed with the work
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Quote Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Scope of Work */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Scope of Work</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 whitespace-pre-wrap">
                {quote.description || "No description provided"}
              </p>
            </CardContent>
          </Card>

          {/* Line Items */}
          {quote.line_items && quote.line_items.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Cost Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-2 font-semibold text-slate-600">Description</th>
                        <th className="text-right py-3 px-2 font-semibold text-slate-600">Qty</th>
                        <th className="text-right py-3 px-2 font-semibold text-slate-600">Unit Price</th>
                        <th className="text-right py-3 px-2 font-semibold text-slate-600">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quote.line_items.map((item, idx) => (
                        <tr key={idx} className="border-b border-slate-100">
                          <td className="py-3 px-2 text-slate-900">{item.description}</td>
                          <td className="py-3 px-2 text-right text-slate-600">{item.quantity || 1}</td>
                          <td className="py-3 px-2 text-right text-slate-600">
                            {formatCurrency(item.unit_price || item.rate)}
                          </td>
                          <td className="py-3 px-2 text-right font-medium text-slate-900">
                            {formatCurrency(item.total || (item.quantity || 1) * (item.unit_price || item.rate || 0))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Totals */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quote Total</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Subtotal (ex VAT)</span>
                  <span className="font-medium">{formatCurrency(quote.subtotal)}</span>
                </div>
                {quote.contingency > 0 && (
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-600">Contingency</span>
                    <span className="font-medium">{formatCurrency(quote.contingency)}</span>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">VAT ({quote.vat_rate || 20}%)</span>
                  <span className="font-medium">{formatCurrency(quote.vat_amount)}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-lg font-semibold text-slate-900">Total (inc VAT)</span>
                  <span className="text-lg font-bold text-slate-900">{formatCurrency(quote.total_inc_vat)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          {quote.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 whitespace-pre-wrap">{quote.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Actions & Info */}
        <div className="space-y-6">
          {/* Action Buttons */}
          {canTakeAction && (
            <Card className="border-orange-200 bg-orange-50/50">
              <CardHeader>
                <CardTitle className="text-lg">Your Decision</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  className="w-full bg-green-600 hover:bg-green-700"
                  onClick={() => setApproveDialogOpen(true)}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve Quote
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-red-200 text-red-600 hover:bg-red-50"
                  onClick={() => setRejectDialogOpen(true)}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject Quote
                </Button>
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => setClarifyDialogOpen(true)}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Request Clarification
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Quote Status */}
          {!canTakeAction && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Status</CardTitle>
              </CardHeader>
              <CardContent>
                {quote.status === "Approved" && (
                  <div className="flex items-center gap-3 text-green-700">
                    <CheckCircle className="w-6 h-6" />
                    <div>
                      <p className="font-medium">Approved</p>
                      <p className="text-sm text-green-600">
                        {quote.approved_at && `on ${formatDate(quote.approved_at)}`}
                        {quote.approved_by && ` by ${quote.approved_by}`}
                      </p>
                    </div>
                  </div>
                )}
                {quote.status === "Rejected" && (
                  <div>
                    <div className="flex items-center gap-3 text-red-700 mb-2">
                      <XCircle className="w-6 h-6" />
                      <div>
                        <p className="font-medium">Rejected</p>
                        <p className="text-sm text-red-600">
                          {quote.rejected_at && `on ${formatDate(quote.rejected_at)}`}
                        </p>
                      </div>
                    </div>
                    {quote.rejection_reason && (
                      <p className="text-sm text-slate-600 mt-2">
                        Reason: {quote.rejection_reason}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Quote Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quote Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-slate-500">Contractor</p>
                <p className="font-medium">{quote.contractor_name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Park / Site</p>
                <p className="font-medium">{quote.park_name}</p>
              </div>
              {quote.unit_name && (
                <div>
                  <p className="text-sm text-slate-500">Unit</p>
                  <p className="font-medium">{quote.unit_name}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-slate-500">Submitted</p>
                <p className="font-medium">{formatDate(quote.submitted_at)}</p>
              </div>
              {quote.valid_until && (
                <div>
                  <p className="text-sm text-slate-500">Valid Until</p>
                  <p className="font-medium">{formatDate(quote.valid_until)}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Linked Job */}
          {quote.job && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Linked Job</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm text-slate-500">Reference</p>
                    <p className="font-medium">{quote.job.reference}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Type</p>
                    <p className="font-medium">{quote.job.job_type || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <p className="font-medium">{quote.job.status}</p>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Link to={`/client-v2/jobs/${quote.job.id}`} className="w-full">
                  <Button variant="outline" className="w-full">View Job Details</Button>
                </Link>
              </CardFooter>
            </Card>
          )}
        </div>
      </div>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Quote</DialogTitle>
            <DialogDescription>
              Approving this quote will authorize the contractor to proceed with the work.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-slate-600">Quote Total</p>
              <p className="text-2xl font-bold text-slate-900">{formatCurrency(quote.total_inc_vat)}</p>
            </div>
            <Textarea
              placeholder="Add notes (optional)"
              value={approveNotes}
              onChange={(e) => setApproveNotes(e.target.value)}
              className="min-h-[80px]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={handleApprove}
              disabled={approving}
            >
              {approving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Confirm Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Quote</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this quote.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Reason for rejection (required)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="min-h-[120px]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={rejecting || !rejectReason.trim()}
            >
              {rejecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
              Confirm Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clarification Dialog */}
      <Dialog open={clarifyDialogOpen} onOpenChange={setClarifyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Clarification</DialogTitle>
            <DialogDescription>
              The contractor will be notified to address your questions.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="What questions do you have about this quote?"
              value={clarifyQuestions}
              onChange={(e) => setClarifyQuestions(e.target.value)}
              className="min-h-[120px]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClarifyDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleRequestClarification}
              disabled={requesting || !clarifyQuestions.trim()}
            >
              {requesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
              Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ClientLayout>
  );
}
